import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MDBox from "components/MDBox";
import CanvasContainer from "../../canvas/CanvasContainer.tsx";
import { GetPublishedSlidesWithContentByTitleIdPublic } from "../../redux/api/universities.api";
import { basicURL } from "../../apiManager/APIConfig";

// Use the standard web slide size; the container will scale responsively
const WEB_STAGE_WIDTH = 1280;
const WEB_STAGE_HEIGHT = 720;

// Derive a normalized 3D model URL from a slide (mirrors ShowThreeD logic)
function getModelUrlFromSlide(slide) {
  if (!slide) return null;
  const files = slide?.Files ?? slide?.files ?? [];
  const first = Array.isArray(files) ? files[0] : null;
  let url = first?.url ?? first?.path ?? null;
  if (url != null && typeof url === "string") {
    const trimmed = url.trim();
    if (trimmed) {
      if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
        const base = (basicURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
        url = trimmed.startsWith("/") ? base + trimmed : `${base}/${trimmed}`;
      } else {
        url = trimmed;
      }
    } else {
      url = null;
    }
  } else {
    url = null;
  }
  return url;
}

function PresentationMobileViewPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const theme = useTheme();
  const allSlides = useSelector((state) => state.uni?.slides || []);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialIndexFromUrl = Number(searchParams.get("slideIndex") || "0");
  const [currentIndex, setCurrentIndex] = useState(
    Number.isNaN(initialIndexFromUrl) || initialIndexFromUrl < 0
      ? 0
      : initialIndexFromUrl
  );

  useEffect(() => {
    if (id && id !== "undefined") {
      GetPublishedSlidesWithContentByTitleIdPublic(id);
    }
  }, [id, dispatch]);

  const { currentSlide, canvasData, isThreeDSlide, threeDModelUrl } = useMemo(() => {
    if (!Array.isArray(allSlides) || allSlides.length === 0) {
      return {
        currentSlide: null,
        canvasData: [],
        isThreeDSlide: false,
        threeDModelUrl: null,
      };
    }
    const safeIndex = Math.min(
      Math.max(currentIndex, 0),
      allSlides.length - 1
    );
    const slide = allSlides[safeIndex];
    const data = slide?.content?.data;
    const type =
      slide?.parentTitle?.type ||
      slide?.Titles?.type ||
      slide?.titles?.type ||
      slide?.type ||
      "";
    const isThreeD = type === "three_d_model";
    const modelUrl = isThreeD ? getModelUrlFromSlide(slide) : null;
    return {
      currentSlide: slide,
      canvasData: Array.isArray(data) ? data : [],
      isThreeDSlide: isThreeD && Boolean(modelUrl),
      threeDModelUrl: modelUrl,
    };
  }, [allSlides, currentIndex]);

  // Force CanvasContainer to remount once slides are loaded so it picks up initialData
  const canvasKey = useMemo(() => {
    if (currentSlide?.id) return currentSlide.id;
    return id || "presentation-mobile-view";
  }, [currentSlide, id]);

  return (
    <MDBox
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        boxSizing: "border-box",
      }}
    >
      <MDBox
        sx={{
          width: "100%",
          maxWidth: WEB_STAGE_WIDTH,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: 3,
          bgcolor: theme.palette.background.paper,
          position: "relative",
        }}
      >
        {/* If this is a dedicated 3D slide, use the same iframe frame as the presentation 3D viewer */}
        {isThreeDSlide && threeDModelUrl ? (
          <MDBox
            sx={{
              width: "100%",
              height: "80vh",
              minHeight: 360,
              borderRadius: 0,
              overflow: "hidden",
              bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5",
            }}
          >
            <iframe
              key={threeDModelUrl}
              src={
                typeof window !== "undefined"
                  ? `${window.location.origin}/model-viewer.html?src=${encodeURIComponent(
                      threeDModelUrl
                    )}&theme=${theme.palette.mode === "dark" ? "dark" : "light"}`
                  : ""
              }
              title="3D Model viewer"
              style={{
                width: "100%",
                height: "100%",
                minHeight: 360,
                border: "none",
                display: "block",
              }}
            />
          </MDBox>
        ) : (
          <CanvasContainer
            key={canvasKey}
            initialData={canvasData}
            isPlayMode
            showAddButtons={false}
            stageWidth={WEB_STAGE_WIDTH}
            stageHeight={WEB_STAGE_HEIGHT}
            viewMode="web"
          />
        )}
      </MDBox>
    </MDBox>
  );
}

export default PresentationMobileViewPage;

